import React, { useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { ThemeProvider } from '@mui/material/styles'
import { CssBaseline, useMediaQuery } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import 'dayjs/locale/es'

import { lightTheme, darkTheme } from './theme'
import useUIStore from './store/uiStore'
import useAuthStore from './store/authStore'

// Layout components
import Layout from './components/Layout/Layout'
import AuthGuard from './components/Auth/AuthGuard'
import PublicRoute from './components/Auth/PublicRoute'

// Pages
import LoginPage from './pages/Auth/LoginPage'
import DashboardPage from './pages/Dashboard/DashboardPage'
import ClientsPage from './pages/Clients/ClientsPage'
import ClientDetailPage from './pages/Clients/ClientDetailPage'
import PurchaseOrdersPage from './pages/PurchaseOrders/PurchaseOrdersPage'
import InvoicesPage from './pages/Invoices/InvoicesPage'
import InvoiceDetailPage from './pages/Invoices/InvoiceDetailPage'
import ReportsPage from './pages/Reports/ReportsPage'
import UsersPage from './pages/Users/UsersPage'
import NotificationsPage from './pages/Notifications/NotificationsPage'
import SettingsPage from './pages/Settings/SettingsPage'
import NotFoundPage from './pages/NotFoundPage'

function App() {
  const { darkMode, setIsMobile } = useUIStore()
  const { isAuthenticated } = useAuthStore()
  
  // Detect mobile screen
  const isMobile = useMediaQuery('(max-width:960px)')
  
  useEffect(() => {
    setIsMobile(isMobile)
  }, [isMobile, setIsMobile])

  // Select theme based on dark mode preference
  const theme = darkMode ? darkTheme : lightTheme

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
        <Routes>
          {/* Public routes */}
          <Route 
            path="/login" 
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            } 
          />
          
          {/* Protected routes */}
          <Route 
            path="/*" 
            element={
              <AuthGuard>
                <Layout>
                  <Routes>
                    {/* Dashboard */}
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/dashboard" element={<DashboardPage />} />
                    
                    {/* Clients */}
                    <Route path="/clients" element={<ClientsPage />} />
                    <Route path="/clients/:id" element={<ClientDetailPage />} />
                    
                    {/* Purchase Orders */}
                    <Route path="/purchase-orders" element={<PurchaseOrdersPage />} />
                    
                    {/* Invoices */}
                    <Route path="/invoices" element={<InvoicesPage />} />
                    <Route path="/invoices/:id" element={<InvoiceDetailPage />} />
                    
                    {/* Reports */}
                    <Route path="/reports" element={<ReportsPage />} />
                    
                    {/* Users */}
                    <Route path="/users" element={<UsersPage />} />
                    
                    {/* Notifications */}
                    <Route path="/notifications" element={<NotificationsPage />} />
                    
                    {/* Settings */}
                    <Route path="/settings" element={<SettingsPage />} />
                    
                    {/* 404 */}
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </Layout>
              </AuthGuard>
            } 
          />
        </Routes>
      </LocalizationProvider>
    </ThemeProvider>
  )
}

export default App
